<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouterStore } from '@/store/router'
import { useSalidaTokens } from '@/composables/useSalidaTokens'
import L from 'leaflet'
import { GestureHandling } from 'leaflet-gesture-handling'
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css'
import { useMapTiles } from '@/composables/useMapTiles'
import { usePublicTheme } from '@/composables/usePublicTheme'
import SalidaHeader from '@/components/SalidaPublica/SalidaHeader.vue'
import SalidaLista from '@/components/SalidaPublica/SalidaLista.vue'
import SalidaInfoSheet from '@/components/SalidaPublica/SalidaInfoSheet.vue'
import SalidaMapChips from '@/components/SalidaPublica/SalidaMapChips.vue'
import SalidaTerritorioSheet from '@/components/SalidaPublica/SalidaTerritorioSheet.vue'
import SalidaNombreDialog from '@/components/SalidaPublica/SalidaNombreDialog.vue'
import SalidaNavModal from '@/components/SalidaPublica/SalidaNavModal.vue'
import SalidaImprimir from '@/components/SalidaPublica/SalidaImprimir.vue'
import SalidaDetalleSimple from '@/components/SalidaPublica/SalidaDetalleSimple.vue'
import SalidaInformarSimple from '@/components/SalidaPublica/SalidaInformarSimple.vue'
import { DEFAULT_CENTER } from '@/store/mapConstants'

// leaflet-gesture-handling: registrar el hook una sola vez por carga de aplicación.
// L.Map y Lvue.Map en map.js son la misma clase (mismo módulo leaflet),
// así que la guarda evita el doble registro si ambos módulos cargan.
if (!L.Map.__gestureHandlingRegistered) {
  L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling)
  L.Map.__gestureHandlingRegistered = true
}

usePublicTheme()
const route = useRouterStore()
const { fetchPublicSalida, postWorkRecord, getWorkedManzanas } = useSalidaTokens()

const token = computed(() => route.params.token)

// ─── Estado principal ───────────────────────────────────────────────────────
const loading = ref(true)
const errorMsg = ref(null)
const data = ref(null)
const activeSalidaIdx = ref(null)
const detailVisible = ref(false)
const showPrint = ref(false)

// El "mode" de selectSalida (ver detalle de un solo territorio para informar)
// no vive en la URL: se aplica una sola vez, apenas se abre el mapa.
let pendingInformar = false

// ─── Vistas simples (QR "ver qué falta" e "informar" de la hoja impresa) ────
// Sin mapa ni chrome: pensadas para gente que no maneja bien la tecnología.
const simpleView = computed(() => {
  if (!route.params.territorio) return null
  if (route.params.accion === 'detalle') return 'detalle'
  if (route.params.accion === 'informar') return 'informar'
  return null
})

const simpleTerritorio = computed(() => {
  const key = route.params.territorio
  if (!key || !data.value?.territories) return null
  return data.value.territories.find((t) => getTerrKey(t) === key) || null
})

const simpleSalidaFecha = computed(() => {
  const sid = route.params.salida
  const s = data.value?.salidas?.find((s) => String(s.id) === String(sid))
  return s?.date || null
})

const simpleSalidaHora = computed(() => {
  const sid = route.params.salida
  const s = data.value?.salidas?.find((s) => String(s.id) === String(sid))
  return s?.hour || null
})

const simpleWorkedManzanas = ref([])

const loadSimpleManzanas = async () => {
  const terrKey = route.params.territorio
  if (!terrKey) return
  try {
    const res = await getWorkedManzanas(token.value)
    simpleWorkedManzanas.value = res?.manzanas?.[terrKey] || []
  } catch {
    simpleWorkedManzanas.value = []
  }
}

// ─── Nombre del usuario ─────────────────────────────────────────────────────
const showNameDialog = ref(false)
const userName = ref('')

const nameKey = computed(() => `salida-name-${token.value}`)

// El nombre es opcional: solo se carga si ya estaba guardado.
// El diálogo se abre recién cuando hace falta (registrar trabajo) o desde el header.
const pendingRegister = ref(false)

const loadName = () => {
  const saved = localStorage.getItem(nameKey.value)
  if (saved) userName.value = saved
}

const confirmName = (name) => {
  userName.value = name
  localStorage.setItem(nameKey.value, name)
  showNameDialog.value = false
  if (pendingRegister.value) {
    pendingRegister.value = false
    registerWork()
  }
}

// Si el usuario cierra el diálogo sin confirmar, se descarta el registro pendiente
watch(showNameDialog, (visible) => {
  if (!visible) pendingRegister.value = false
})

// ─── Helpers de fecha ────────────────────────────────────────────────────────
const getDayName = (d) => {
  if (!d) return ''
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
  return days[new Date(d).getDay()]
}

// ─── Navegación lista ↔ detalle ─────────────────────────────────────────────
const showInfoModal = ref(false)
// 'completo': información de la salida. 'pendientes': qué falta trabajar.
const infoModalMode = ref('completo')

const abrirInfo = (modo) => {
  infoModalMode.value = modo
  showInfoModal.value = true
}

// Manzanas y territorios que todavía no se trabajaron en la salida activa.
const pendientesPorTerritorio = computed(() =>
  activeTerritories.value.map((terr) => {
    const key = getTerrKey(terr)
    const mzIdxs = getTerritoryManzanaIndexes(terr)
    const hechas = workedManzanas.value[key] || []
    const pendientes = mzIdxs.filter((i) => !hechas.includes(i))
    const trabajado = mzIdxs.length
      ? pendientes.length === 0
      : workedTerritories.value.includes(key)
    return { key, total: mzIdxs.length, pendientes, trabajado }
  })
)

// Abre/cierra el detalle (mapa) actualizando route.params.salida: así el
// cambio de pantalla queda en el historial del navegador y el botón
// atrás/adelante funciona entre la lista y el detalle de una salida.
const selectSalida = (idx, mode) => {
  const salida = data.value?.salidas?.[idx]
  if (!salida) return
  if (mode === 'informar') pendingInformar = true
  const next = { ...route.params, salida: salida.id }
  delete next.territorio
  delete next.accion
  route.params = next
}

const goBack = () => {
  const next = { ...route.params }
  delete next.salida
  delete next.territorio
  delete next.accion
  route.params = next
}

// Aplica el estado de "detalle abierto" (mapa) según route.params.salida —
// se usa tanto al montar como al reaccionar a cambios de historial.
const applyDetailFromParams = async () => {
  if (!data.value) return
  const salidaId = route.params.salida
  if (salidaId) {
    if (String(activeSalida.value?.id) === String(salidaId)) return
    const idx = data.value.salidas?.findIndex((s) => String(s.id) === String(salidaId))
    if (idx == null || idx < 0) return
    activeSalidaIdx.value = idx
    detailVisible.value = true
    await nextTick()
    await initMap()
    loadName()
    openWs()
    if (pendingInformar) {
      pendingInformar = false
      // Con un solo territorio se abre directo el registro; con varios se deja
      // que el usuario elija el territorio tocándolo en el mapa.
      if (activeTerritories.value.length === 1) {
        selectedTerritory.value = getTerrKey(activeTerritories.value[0])
      }
    }
  } else if (detailVisible.value) {
    stopTracking()
    selectedTerritory.value = null
    selectedTerrPointIndex.value = null
    workedTerritories.value = []
    polygonsByKey = {}
    manzanaMarkersMap = {}
    terrPointMarkers = {}
    detailVisible.value = false
    activeSalidaIdx.value = null
    showInfoModal.value = false
  }
}

watch(() => route.params.salida, applyDetailFromParams)

// Vista de impresión: también queda en la URL (route.params.vista) para
// registrar el paso en el historial del navegador.
// Botón de imprimir oculto por ahora.
// eslint-disable-next-line no-unused-vars
const openPrint = () => {
  route.params = { ...route.params, vista: 'imprimir' }
}

const closePrint = () => {
  const next = { ...route.params }
  delete next.vista
  route.params = next
}

watch(() => route.params.vista, (v) => { showPrint.value = v === 'imprimir' })

// Cambia a la versión de la página pensada para personas mayores (Portal
// Fácil), conservando el token y, si había una salida/territorio abiertos,
// aterrizando ahí mismo en la otra vista.
const irAVistaMayores = () => {
  route.params = {
    ...route.params,
    ...(activeSalida.value ? { salida: activeSalida.value.id } : {}),
    ...(selectedTerritory.value ? { territorio: selectedTerritory.value } : {}),
  }
  route.page = 'salidaPublicaMayores'
}

// ─── Registro de trabajo ─────────────────────────────────────────────────────
const selectedTerritory = ref(null)
const workNotes = ref('')
const workSaving = ref(false)
const workSuccess = ref(false)
const workError = ref(null)
const workedTerritories = ref([])
let polygonsByKey = {}
let terrPointMarkers = {}

const registerWork = async () => {
  if (!selectedTerritory.value) return
  if (!userName.value) {
    pendingRegister.value = true
    showNameDialog.value = true
    return
  }
  const manzanas = workedManzanas.value[selectedTerritory.value] || []
  if (!manzanas.length) {
    workError.value = 'Selecciona al menos una manzana trabajada.'
    return
  }
  workSaving.value = true
  workSuccess.value = false
  workError.value = null
  const salida = data.value?.salidas?.[activeSalidaIdx.value]
  const res = await postWorkRecord(token.value, {
    territoryKey: selectedTerritory.value,
    conductor: userName.value,
    fecha: salida?.date || Date.now(),
    notas: workNotes.value,
    manzanas,
  })
  workSaving.value = false
  if (res.error) {
    workError.value = res.error
  } else {
    workSuccess.value = true
    workNotes.value = ''
    const key = selectedTerritory.value
    if (!workedTerritories.value.includes(key)) {
      workedTerritories.value.push(key)
    }
    const poly = polygonsByKey[key]
    if (poly && leafletMap) {
      const terrColor = selectedTerritoryData.value?.color?.backgroundColor
      poly.setStyle({ color: terrColor || 'rgb(156,156,0)', fillColor: terrColor || 'rgb(252,252,71)', fillOpacity: 0.45, opacity: 0.9, weight: 3 })
      const center = poly.getBounds().getCenter()
      L.marker(center, {
        icon: L.divIcon({
          className: '',
          iconSize: [28, 28],
          iconAnchor: [14, 14],
          html: '<div style="width:28px;height:28px;border-radius:50%;background:#2e7d32;color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px;box-shadow:0 2px 6px rgba(0,0,0,.3);font-weight:bold">✓</div>',
        }),
        zIndexOffset: 500,
      }).addTo(leafletMap)
    }
    if (wsConn?.readyState === WebSocket.OPEN) {
      wsConn.send(JSON.stringify({
        type: 'manzana_update',
        terrKey: key,
        manzanas: workedManzanas.value[key] || [],
      }))
    }
    setTimeout(() => {
      workSuccess.value = false
      selectedTerritory.value = null
    }, 1500)
  }
}

// ─── Salida activa ──────────────────────────────────────────────────────────
const activeSalida = computed(() => data.value?.salidas?.[activeSalidaIdx.value] || null)

// Nombre del conductor de la salida activa, mostrado en el header
// en lugar del título genérico "Salida de Predicación".
const captainName = computed(() => {
  const id = activeSalida.value?.conductor
  if (!id) return ''
  return data.value?.config?.conductores?.find((c) => c.id === id)?.name || ''
})

// En el detalle el header fijo muestra día y hora; el conductor va en la barra de abajo.
const headerTitle = computed(() => {
  if (!detailVisible.value) return null
  const d = activeSalida.value?.date
  if (!d) return 'Salida'
  const hora = activeSalida.value?.hour
  return `${getDayName(d)} ${new Date(d).getDate()}${hora ? ` · ${hora} hs` : ''}`
})

// ─── Helpers de territorio ───────────────────────────────────────────────────
const getTerrKey = (terr) => `${terr.zona || ''}${terr.numero || ''}`

const selectedTerritoryData = computed(() =>
  activeTerritories.value.find(t => getTerrKey(t) === selectedTerritory.value) || null
)

const selectedTerritoryColor = computed(() =>
  selectedTerritoryData.value?.color?.backgroundColor || '#007aff'
)

// ─── Manzanas ────────────────────────────────────────────────────────────────
const workedManzanas = ref({})
let manzanaMarkersMap = {}

const getTerritoryManzanaIndexes = (terr) => {
  const mz = terr?.mzNumbers
  if (!Array.isArray(mz) || mz.length <= 1) return []
  const result = []
  for (let i = 1; i < mz.length; i++) {
    if (mz[i]?.[0]) result.push(i)
  }
  return result
}

const selectedTerritoryManzanas = computed(() => getTerritoryManzanaIndexes(selectedTerritoryData.value))

const isManzanaWorked = (terrKey, mzIdx) =>
  (workedManzanas.value[terrKey] || []).includes(mzIdx)

const workedManzanasCount = computed(() =>
  (workedManzanas.value[selectedTerritory.value] || []).length
)

// Fondo y texto fijos (no siguen el tema claro/oscuro de la app): el marcador
// vive sobre el mapa, cuyos tiles tienen su propia paleta clara u oscura según
// el proveedor, independiente del tema de la UI (igual que .terr-label).
const buildManzanaIconHtml = (terrKey, mzIdx, terr) => {
  const worked = isManzanaWorked(terrKey, mzIdx)
  const opacity = worked ? 0.3 : 1
  const borderStyle = terr?.color?.backgroundColor
    ? `border: ${terr.color.backgroundColor} 1px solid;`
    : ''
  return `<div class="icon manzana-marker-center" style="font-size: 1em; ${borderStyle} opacity: ${opacity}; background: #fff; color: #1c1c1e;">${mzIdx}</div>`
}

const refreshManzanaMarker = (terrKey, mzIdx) => {
  const marker = manzanaMarkersMap[`${terrKey}_${mzIdx}`]
  if (!marker) return
  const terr = activeTerritories.value.find(t => getTerrKey(t) === terrKey)
  marker.setIcon(L.divIcon({
    className: 'manzana-marker',
    iconSize: null,
    html: buildManzanaIconHtml(terrKey, mzIdx, terr),
  }))
}

const toggleManzana = (terrKey, mzIdx) => {
  const current = workedManzanas.value[terrKey] || []
  const worked = current.includes(mzIdx)
  workedManzanas.value = {
    ...workedManzanas.value,
    [terrKey]: worked ? current.filter(i => i !== mzIdx) : [...current, mzIdx],
  }
  refreshManzanaMarker(terrKey, mzIdx)
}

// ─── Territorios de la salida activa ────────────────────────────────────────
const activeTerritories = computed(() => {
  if (!activeSalida.value || !data.value?.territories) return []
  const keys = activeSalida.value.territorios || []
  return data.value.territories.filter((t) => {
    const key1 = `${t.zona}${t.numero}`
    const key2 = `${t.zona} ${t.numero}`
    const key3 = String(t.numero)
    return keys.some(
      (k) => k === key1 || k === key2 || k === key3 || k === t.zona
    )
  })
})

// Todos los puntos de encuentro de los territorios asignados (salidas "De
// Grupo Continua" no tienen un único punto de encuentro: se muestran todos,
// numerados para poder elegirlos en un selector).
const territoryMeetingPoints = computed(() =>
  activeSalida.value?.type !== 'grupoContinuo'
    ? []
    : activeTerritories.value
    .flatMap((t) =>
      (t.puntoEncuentro || [])
        .filter((p) => p.latlng)
        .map((p) => ({ ...p, terrKey: getTerrKey(t) }))
    )
    .map((p, i) => ({ ...p, index: i + 1 }))
)

// Punto de encuentro elegido puntualmente en el selector de SalidaInfoSheet: si
// hay uno, prevalece sobre el resto de puntos del territorio.
const selectedTerrPointIndex = ref(null)

// Muestra en el mapa únicamente el punto elegido en el selector (si hay uno),
// o si no, todos los puntos del territorio seleccionado; si no hay territorio
// seleccionado no se muestra ninguno.
const updateTerrPointMarkersVisibility = () => {
  if (!leafletMap) return
  const sel = selectedTerritory.value
  const selPoint = selectedTerrPointIndex.value
  for (const p of territoryMeetingPoints.value) {
    const marker = terrPointMarkers[p.index]
    if (!marker) continue
    const visible = selPoint != null ? p.index === selPoint : !!sel && p.terrKey === sel
    if (visible && !leafletMap.hasLayer(marker)) marker.addTo(leafletMap)
    else if (!visible && leafletMap.hasLayer(marker)) leafletMap.removeLayer(marker)
  }
}

// Cambiar de territorio descarta la selección puntual de punto, salvo que
// coincida con el territorio del punto recién elegido (ver centerOnTerrPoint).
watch(selectedTerritory, (newVal) => {
  const currentPoint = territoryMeetingPoints.value.find((p) => p.index === selectedTerrPointIndex.value)
  if (!currentPoint || currentPoint.terrKey !== newVal) selectedTerrPointIndex.value = null
  updateTerrPointMarkersVisibility()
})

watch(selectedTerrPointIndex, updateTerrPointMarkersVisibility)

// Centra el mapa en el punto de encuentro de territorio elegido en el
// selector de SalidaInfoSheet, y abre su popup.
const centerOnTerrPoint = (point) => {
  const ll = toLatlng(point?.latlng)
  if (!ll || !leafletMap) return
  selectedTerritory.value = point.terrKey || selectedTerritory.value
  selectedTerrPointIndex.value = point.index
  leafletMap.setView(ll, 17)
  terrPointMarkers[point.index]?.openPopup()
  showInfoModal.value = false
}

// ─── Handlers de chips del mapa ──────────────────────────────────────────────
const fitTerritory = (terrKey) => {
  const poly = polygonsByKey[terrKey]
  if (poly && leafletMap) leafletMap.fitBounds(poly.getBounds().pad(0.15))
}

const centerOnTerritory = (terrKey) => {
  selectedTerritory.value = terrKey
  fitTerritory(terrKey)
}

const centerOnPE = () => {
  const ll = toLatlng(activeSalida.value?.puntoEncuentro?.latlng)
  if (ll && leafletMap) leafletMap.setView(ll, 17)
}

const centerOnPET = () => {
  const ll = toLatlng(activeSalida.value?.puntoEncuentroTerritorio?.latlng)
  if (ll && leafletMap) leafletMap.setView(ll, 17)
}

// Redirige al GPS (Google Maps) con las coordenadas vigentes del punto de
// encuentro en territorio, recién leídas de la salida (no coordenadas fijas
// guardadas en el enlace, ya que el punto puede cambiar).
const redirectToGpsPET = () => {
  const ll = toLatlng(activeSalida.value?.puntoEncuentroTerritorio?.latlng)
  if (!ll) return
  const dest = `${ll[0]},${ll[1]}`
  const url = userLatLng.value
    ? `https://www.google.com/maps/dir/?api=1&origin=${userLatLng.value[0]},${userLatLng.value[1]}&destination=${dest}`
    : `https://www.google.com/maps/dir/?api=1&destination=${dest}`
  window.location.href = url
}

// ─── Modal de navegación ─────────────────────────────────────────────────────
const showNavModal = ref(false)

// ─── Aplicar estado de manzanas recibido por WS ──────────────────────────────
const applyManzanaSnapshot = (manzanas) => {
  if (!manzanas || typeof manzanas !== 'object') return
  const next = { ...workedManzanas.value }
  for (const [terrKey, mzList] of Object.entries(manzanas)) {
    if (!Array.isArray(mzList) || !mzList.length) continue
    next[terrKey] = [...new Set([...(next[terrKey] || []), ...mzList.map(Number)])]
  }
  workedManzanas.value = next
  for (const [terrKey, mzList] of Object.entries(manzanas)) {
    for (const mzIdx of mzList) refreshManzanaMarker(terrKey, Number(mzIdx))
  }
}

const applyManzanaUpdate = (terrKey, manzanas) => {
  if (!terrKey || !Array.isArray(manzanas) || !manzanas.length) return
  const current = workedManzanas.value[terrKey] || []
  workedManzanas.value = {
    ...workedManzanas.value,
    [terrKey]: [...new Set([...current, ...manzanas.map(Number)])],
  }
  for (const mzIdx of manzanas) refreshManzanaMarker(terrKey, Number(mzIdx))
}

// ─── Geolocalización ──────────────────────────────────────────────────────────
const hasGeolocation = typeof navigator !== 'undefined' && 'geolocation' in navigator

const userLatLng = ref(null)
const trackingLocation = ref(false)

let userMarker = null
let geoWatchId = null
let wsConn = null

const { getWsUrl } = useSalidaTokens()

// ─── WebSocket siempre abierto (manzanas) ────────────────────────────────────
let wsUnmounted = false

const openWs = () => {
  if (wsUnmounted) return
  if (wsConn?.readyState === WebSocket.OPEN || wsConn?.readyState === WebSocket.CONNECTING) return
  wsConn = new WebSocket(getWsUrl(token.value))
  wsConn.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data)
      if (msg.type === 'manzana_snapshot') applyManzanaSnapshot(msg.manzanas)
      else if (msg.type === 'manzana_update') applyManzanaUpdate(msg.terrKey, msg.manzanas)
    } catch {}
  }
  wsConn.onerror = () => {}
  wsConn.onclose = () => {
    if (!wsUnmounted) setTimeout(openWs, 3000)
  }
}

const startTracking = () => {
  if (!hasGeolocation || trackingLocation.value) return
  trackingLocation.value = true
  geoWatchId = navigator.geolocation.watchPosition(
    (pos) => {
      const ll = [pos.coords.latitude, pos.coords.longitude]
      userLatLng.value = ll
      if (!leafletMap) return
      if (userMarker) {
        userMarker.setLatLng(ll)
      } else {
        userMarker = L.marker(ll, {
          icon: L.icon({ iconUrl: '/p2.png', iconSize: [32, 32] }),
          zIndexOffset: 1000,
        }).addTo(leafletMap)
      }
    },
    () => { trackingLocation.value = false },
    { enableHighAccuracy: true }
  )
}

const stopTracking = () => {
  if (geoWatchId != null) { navigator.geolocation.clearWatch(geoWatchId); geoWatchId = null }
  userMarker?.remove()
  userMarker = null
  trackingLocation.value = false
  userLatLng.value = null
}

const handleWhereAmI = () => {
  if (!hasGeolocation) {
    alert('Tu navegador no permite acceder a la ubicación. Abrí el enlace con una conexión segura (https).')
    return
  }
  if (!trackingLocation.value) {
    startTracking()
    const wait = setInterval(() => {
      if (userLatLng.value && leafletMap) {
        leafletMap.setView(userLatLng.value, 17)
        clearInterval(wait)
      }
    }, 500)
    setTimeout(() => clearInterval(wait), 10000)
  } else if (userLatLng.value && leafletMap) {
    leafletMap.setView(userLatLng.value, 17)
  }
}

onUnmounted(() => {
  wsUnmounted = true
  stopTracking()
  wsConn?.close()
  wsConn = null
})

// ─── Mapa Leaflet ────────────────────────────────────────────────────────────
const mapContainer = ref(null)
let leafletMap = null
let polygonLayers = []

const { isSatellite, googleEnabled, applyCurrentTiles, toggle: toggleMapLayer, toggleGoogle } = useMapTiles(() => leafletMap, { local: true })

const toLatlng = (v) => {
  if (!v) return null
  if (Array.isArray(v)) return [v[0], v[1]]
  return [Number(v.lat), Number(v.lng)]
}

// Anillo exterior que cubre el mundo; los territorios de la salida se agregan
// como agujeros, dejando una máscara oscura sobre todo lo que no es territorio.
const WORLD_RING = [
  [-90, -360],
  [90, -360],
  [90, 360],
  [-90, 360],
]

const initMap = async () => {
  await nextTick()
  if (!mapContainer.value) return

  if (leafletMap) {
    leafletMap.remove()
    leafletMap = null
    polygonLayers = []
    polygonsByKey = {}
    manzanaMarkersMap = {}
    terrPointMarkers = {}
    userMarker = null
  }

  const territories = activeTerritories.value
  const salida = activeSalida.value

  const defaultCenter = salida?.puntoEncuentro?.latlng
    ? toLatlng(salida.puntoEncuentro.latlng)
    : territoryMeetingPoints.value[0]?.latlng
      ? toLatlng(territoryMeetingPoints.value[0].latlng)
      : DEFAULT_CENTER

  leafletMap = L.map(mapContainer.value, {
    gestureHandling: true,
    scrollWheelZoom: false,
  }).setView(defaultCenter, 15)

  applyCurrentTiles(leafletMap)

  const maskHoles = []
  for (const terr of territories) {
    if (!terr.limits?.length) continue
    const coords = terr.limits.map(toLatlng).filter(Boolean)
    if (coords.length < 3) continue

    const keyLabel = `${terr.zona || ''}${terr.numero || ''}`
    const alreadyWorked = workedTerritories.value.includes(keyLabel)

    const poly = L.polygon(coords, {
      color: terr.color?.backgroundColor || 'rgb(156,156,0)',
      fillColor: terr.color?.backgroundColor || 'rgb(252,252,71)',
      fillOpacity: alreadyWorked ? 0.45 : 0.1,
      opacity: alreadyWorked ? 0.9 : 0.3,
      weight: alreadyWorked ? 3 : 2,
    }).addTo(leafletMap)

    poly.bindTooltip(keyLabel, { permanent: true, direction: 'center', className: 'terr-label' })
    poly.on('click', () => { selectedTerritory.value = keyLabel })

    polygonsByKey[keyLabel] = poly
    polygonLayers.push(poly)
    maskHoles.push(coords)

    if (Array.isArray(terr.mzNumbers)) {
      for (let i = 1; i < terr.mzNumbers.length; i++) {
        const entry = terr.mzNumbers[i]
        if (!entry?.[0]) continue
        const mapKey = `${keyLabel}_${i}`
        const icon = L.divIcon({
          className: 'manzana-marker',
          iconSize: null,
          html: buildManzanaIconHtml(keyLabel, i, terr),
        })
        const mzMarker = L.marker(entry[0], { icon }).addTo(leafletMap)
        manzanaMarkersMap[mapKey] = mzMarker
        const capturedKey = keyLabel
        // Tocar la manzana solo selecciona el territorio; marcarla como trabajada
        // se hace desde la hoja del territorio.
        mzMarker.on('click', (e) => {
          L.DomEvent.stopPropagation(e)
          selectedTerritory.value = capturedKey
        })
      }
    }
  }

  // 0.45: sobre tiles satelitales (oscuros) una opacidad menor es imperceptible
  if (maskHoles.length) {
    L.polygon([WORLD_RING, ...maskHoles], {
      stroke: false,
      fillColor: '#000000',
      fillOpacity: 0.45,
      interactive: false,
    }).addTo(leafletMap)
  }

  const peIcon = L.icon({ iconUrl: '/pe.svg', iconSize: [32, 44], iconAnchor: [16, 44], popupAnchor: [0, -46] })
  const petIcon = L.icon({ iconUrl: '/pet.svg', iconSize: [32, 44], iconAnchor: [16, 44], popupAnchor: [0, -46] })

  if (salida?.puntoEncuentro?.latlng) {
    const ll = toLatlng(salida.puntoEncuentro.latlng)
    L.marker(ll, { icon: peIcon })
      .addTo(leafletMap)
      .bindPopup(`<b>Punto de encuentro</b><br>${salida.puntoEncuentro.name || salida.puntoEncuentro.direccion || ''}${salida.puntoEncuentro.name && salida.puntoEncuentro.direccion ? '<br><small>' + salida.puntoEncuentro.direccion + '</small>' : ''}`)
  }

  if (salida?.puntoEncuentroTerritorio?.latlng) {
    const ll = toLatlng(salida.puntoEncuentroTerritorio.latlng)
    L.marker(ll, { icon: petIcon })
      .addTo(leafletMap)
      .bindPopup(`<b>Punto en territorio</b><br>${salida.puntoEncuentroTerritorio.name || salida.puntoEncuentroTerritorio.direccion || ''}${salida.puntoEncuentroTerritorio.name && salida.puntoEncuentroTerritorio.direccion ? '<br><small>' + salida.puntoEncuentroTerritorio.direccion + '</small>' : ''}`)
  }

  // Puntos de encuentro de los territorios asignados (salidas "De Grupo Continua")
  for (const p of territoryMeetingPoints.value) {
    const ll = toLatlng(p.latlng)
    if (!ll) continue
    const marker = L.marker(ll, { icon: peIcon })
      .addTo(leafletMap)
      .bindPopup(`<b>${p.index}. ${p.name || p.direccion || 'Punto de encuentro'}</b>${p.direccion && p.name ? '<br><small>' + p.direccion + '</small>' : ''}`)
    terrPointMarkers[p.index] = marker
  }
  updateTerrPointMarkersVisibility()

  if (polygonLayers.length > 0) {
    const group = L.featureGroup(polygonLayers)
    leafletMap.fitBounds(group.getBounds().pad(0.1))
  }
}

// ─── Carga inicial ────────────────────────────────────────────────────────────
onMounted(async () => {
  if (!token.value) {
    errorMsg.value = 'Token no encontrado en la URL.'
    loading.value = false
    return
  }
  try {
    const res = await fetchPublicSalida(token.value)
    if (res.error) {
      errorMsg.value = res.error
    } else {
      data.value = res
      // `loading` debe bajar antes de disparar applyDetailFromParams: initMap()
      // espera un nextTick() para que exista <div ref="mapContainer">, y ese div
      // solo se renderiza cuando loading ya es false (ver v-if/v-else-if del template).
      loading.value = false

      // Los QR "ver qué falta" e "informar" de la hoja impresa llevan a vistas
      // mínimas sin mapa (pensadas para quien no maneja bien la tecnología):
      // no necesitan Leaflet ni WebSocket, solo las manzanas trabajadas.
      if (simpleView.value) {
        await loadSimpleManzanas()
        return
      }

      // El QR "mapa interactivo" de la hoja impresa apunta a una salida y
      // territorio puntuales (?salida=<id>&territorio=<key>): si coincide, se
      // abre su detalle sin pasar por la lista y se centra el mapa en él.
      const targetSalidaId = route.params.salida
      if (targetSalidaId) {
        const idx = data.value.salidas?.findIndex((s) => String(s.id) === String(targetSalidaId))
        if (idx != null && idx > -1) {
          // Enlace "punto de encuentro en territorio" enviado en el mensaje de
          // WhatsApp: lee el punto vigente de la salida (no coordenadas fijas
          // guardadas en el enlace) y redirige directo al GPS, sin pasar por el
          // mapa interactivo.
          if (route.params.accion === 'pet') {
            activeSalidaIdx.value = idx
            redirectToGpsPET()
            return
          }
          if (route.params.accion === 'informar') pendingInformar = true
          await applyDetailFromParams()
          const terrKey = route.params.territorio
          if (terrKey) {
            if (route.params.accion === 'informar') selectedTerritory.value = terrKey
            else fitTerritory(terrKey)
          }
        }
      }
    }
  } catch {
    errorMsg.value = 'Error al cargar los datos de la salida.'
  }
  loading.value = false
})
</script>

<template>
  <div class="min-h-screen surface-ground">
    <template v-if="simpleView === 'detalle'">
      <SalidaDetalleSimple
        :loading="loading"
        :errorMsg="errorMsg"
        :territorio="simpleTerritorio"
        :terrKey="route.params.territorio"
        :workedManzanas="simpleWorkedManzanas"
      />
    </template>
    <template v-else-if="simpleView === 'informar'">
      <SalidaInformarSimple
        :loading="loading"
        :errorMsg="errorMsg"
        :territorio="simpleTerritorio"
        :terrKey="route.params.territorio"
        :workedManzanas="simpleWorkedManzanas"
        :token="token"
        :salidaFecha="simpleSalidaFecha"
        :salidaHora="simpleSalidaHora"
      />
    </template>
    <template v-else-if="!showPrint">
    <SalidaHeader
      :title="headerTitle"
      :congregacion="data?.config?.congregacion?.name"
      :userName="userName"
      @click-user="showNameDialog = true"
      @cambiar-vista="irAVistaMayores"
    />

    <div v-if="loading" class="flex justify-content-center align-items-center" style="height:60vh">
      <i class="pi pi-spin pi-spinner" style="font-size:2rem;color:var(--accent)" />
    </div>

    <div v-else-if="errorMsg" class="flex flex-column justify-content-center align-items-center gap-3" style="height:60vh">
      <i class="pi pi-lock text-color-secondary" style="font-size:3rem" />
      <div class="text-color-secondary text-center">{{ errorMsg }}</div>
    </div>

    <div
      v-else-if="!data?.salidas?.length"
      class="flex flex-column justify-content-center align-items-center gap-2"
      style="height:60vh"
    >
      <i class="pi pi-calendar text-color-secondary" style="font-size:2rem" />
      <div class="text-color-secondary">No hay salidas disponibles en este enlace.</div>
    </div>

    <SalidaLista
      v-else-if="!detailVisible"
      :salidas="data.salidas"
      :conductores="data.config?.conductores"
      :grupos="data.config?.grupos"
      :zonas="data.config?.zonas"
      @select="selectSalida"
    />

    <div v-else>
      <!-- Nav bar -->
      <div class="detail-nav-bar">
        <button class="back-btn" @click="goBack">
          <i class="pi pi-chevron-left" />
          <span>Salidas</span>
        </button>
        <span class="nav-date">{{ captainName || 'Conductor' }}</span>
        <!-- <button class="info-btn" title="Imprimir" @click="openPrint">
          <i class="pi pi-print" />
        </button> -->
        <button class="info-btn" title="Información de la salida" @click="abrirInfo('completo')">
          <i class="pi pi-info-circle" />
        </button>
      </div>

      <section v-if="activeTerritories.length" class="welcome-block">
        <h1 class="welcome-title">¡Bienvenido!</h1>
        <button type="button" class="welcome-cta" @click="abrirInfo('pendientes')">
          <i class="pi pi-map" />
          <span>¿Qué falta trabajar? <strong>Tocá acá</strong></span>
        </button>
        <button type="button" class="welcome-cta" @click="abrirInfo('completo')">
          <i class="pi pi-check-square" />
          <span>¿Querés informar lo trabajado? <strong>Tocá acá</strong></span>
        </button>

        
      </section>

      <section class="map-section">
        <h2 class="map-section-title">Mapa</h2>
        <div class="map-section-actions">
          <button type="button" class="map-action-btn where" :class="{ active: trackingLocation }" @click="handleWhereAmI">
            <i class="pi pi-map-marker" /> ¿Dónde estoy?
          </button>
          <button type="button" class="map-action-btn how" @click="showNavModal = true">
            <i class="pi pi-compass" /> ¿Cómo llegar?
          </button>
        </div>
      </section>

      <div class="relative map-wrap">
        <div ref="mapContainer" style="height:100%;width:100%" />
        <!-- Toggle Calle / Satélite -->
        <button class="layer-toggle-btn" @click="toggleMapLayer" :title="isSatellite ? 'Ver calles' : 'Ver satélite'">
          <i :class="isSatellite ? 'pi pi-map' : 'pi pi-globe'" />
        </button>
        <!-- Toggle mapa de Google -->
        <button
          class="layer-toggle-btn google-toggle-btn"
          :class="{ active: googleEnabled }"
          @click="toggleGoogle"
          :title="googleEnabled ? 'Usar mapa estándar' : 'Usar mapa de Google'"
        >
          <span class="google-g">G</span>
        </button>
        <!-- Flecha al norte: el mapa no rota, así que queda fija en la esquina -->
        <div class="north-arrow" title="Norte">
          <img src="/north.png" alt="Norte" />
        </div>
        <div
          v-if="!activeTerritories.length"
          class="absolute top-0 left-0 w-full h-full flex justify-content-center align-items-center"
          style="background:var(--bg-grouped);opacity:0.9;pointer-events:none"
        >
          <span class="text-color-secondary text-sm">Sin territorios configurados</span>
        </div>
       <!--  <SalidaMapChips
          :activeTerritories="activeTerritories"
          :activeSalida="activeSalida"
          :selectedTerritory="selectedTerritory"
          @select-territory="centerOnTerritory"
          @go-to-pe="centerOnPE"
          @go-to-pet="centerOnPET"
        /> -->
      </div>
    </div>

    <SalidaInfoSheet
      v-model:visible="showInfoModal"
      :mode="infoModalMode"
      :pendientes="pendientesPorTerritorio"
      :salida="activeSalida"
      :conductores="data?.config?.conductores"
      :grupos="data?.config?.grupos"
      :zonas="data?.config?.zonas"
      :territoryMeetingPoints="territoryMeetingPoints"
      :selectedPointIndex="selectedTerrPointIndex"
      :territoryKeys="activeTerritories.map(getTerrKey)"
      @select-point="centerOnTerrPoint"
      @informar="(key) => (selectedTerritory = key)"
    />

    <SalidaTerritorioSheet
      v-if="data"
      :selectedTerritory="selectedTerritory"
      :selectedTerritoryColor="selectedTerritoryColor"
      :workSuccess="workSuccess"
      :workedTerritories="workedTerritories"
      :userName="userName"
      :workSaving="workSaving"
      :workError="workError"
      :selectedTerritoryManzanas="selectedTerritoryManzanas"
      :workedManzanas="workedManzanas"
      :workedManzanasCount="workedManzanasCount"
      v-model:workNotes="workNotes"
      @close="selectedTerritory = null"
      @register="registerWork"
      @toggle-manzana="toggleManzana"
    />

    <SalidaNombreDialog
      v-model:visible="showNameDialog"
      closable
      @confirm="confirmName"
    />

    <SalidaNavModal
      v-model:visible="showNavModal"
      :activeSalida="activeSalida"
      :userLatLng="userLatLng"
      :territoryMeetingPoints="territoryMeetingPoints"
    />
    </template>

    <SalidaImprimir
      v-else
      :salida="activeSalida"
      :territories="activeTerritories"
      :congregacionName="data?.config?.congregacion?.name"
      :conductores="data?.config?.conductores"
      :grupos="data?.config?.grupos"
      :zonas="data?.config?.zonas"
      :printTheme="data?.config?.printTheme"
      :token="token"
      @close="closePrint"
    />
  </div>
</template>

<style scoped>
:deep(.terr-label) {
  background: #000;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  font-weight: bold;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  min-height: 26px;
  padding: 2px;
  text-align: center;
  line-height: 1;
}

:deep(.terr-label)::before {
  display: none;
}

/* Nav bar de vuelta */
.detail-nav-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-primary);
  border-bottom: 1px solid rgba(var(--label-rgb), 0.18);
  padding: 0 8px 0 4px;
  min-height: 48px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--accent);
  font-size: 18px;
  padding: 8px 10px;
  -webkit-tap-highlight-color: transparent;
  border-radius: 8px;
}

.back-btn i {
  font-size: 1rem;
}

.back-btn:active {
  background: rgba(0, 122, 255, 0.08);
}

.nav-date {
  font-size: 14px;
  font-weight: 500;
  color: rgba(var(--label-rgb), 0.55);
  flex: 1;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 4px;
}

.info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--accent);
  font-size: 1.05rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

.info-btn:active {
  background: rgba(0, 122, 255, 0.08);
}

/* Bienvenida (territorios y manzanas asignadas) */
.welcome-block {
  text-align: left;
  background: var(--bg-primary);
  padding: 20px 16px 8px;
}

.welcome-title {
  margin: 0 0 16px;
  font-size: 2rem;
  font-weight: 300;
  line-height: 1.2;
  color: var(--label-primary);
}

.welcome-cta {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: 0 0 12px;
  padding: 14px 16px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: rgba(0, 122, 255, 0.08);
  color: var(--label-primary);
  font-size: 15px;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.welcome-cta i {
  font-size: 1.25rem;
  color: var(--accent);
}

.welcome-cta strong {
  color: var(--accent);
}

.welcome-block,
.welcome-block h1,
.welcome-block p,
.welcome-block .welcome-cta {
  text-align: left !important;
}

.welcome-note {
  margin: 4px 0 0;
  font-size: 12px;
  color: #dc2626;
}

/* Sección "Mapa" */
.map-section {
  text-align: left;
  padding: 12px 12px 8px;
  background: var(--bg-secondary);
}

.map-section-title {
  margin: 0 0 10px 4px;
  font-size: 2rem;
  font-weight: 300;
  color: var(--label-primary);
}

.map-section-actions {
  display: flex;
  gap: 8px;
}

.map-action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.map-action-btn.where {
  background: #fbbf24;
  color: #1c1c1e;
}

.map-action-btn.where.active {
  background: var(--accent);
  color: #fff;
}

.map-action-btn.how {
  background: #8b93d1;
  color: #fff;
}

.map-wrap {
  height: calc(100vh - 120px);
  min-height: 350px;
  margin: 0 12px 12px;
  border-radius: 8px;
  overflow: hidden;
}

/* Botón toggle calle/satélite — esquina superior izquierda del mapa */
.layer-toggle-btn {
  position: absolute;
  top: 84px; /* debajo de los controles de zoom de Leaflet (+/-) */
  left: 10px;
  z-index: 1000;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: var(--bg-primary);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: var(--label-primary);
  box-shadow: 0 1px 5px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.14);
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
}

.layer-toggle-btn:active {
  background: var(--bg-secondary);
}

.google-toggle-btn {
  top: 124px; /* debajo del toggle calle/satélite (84 + 34 + 6 de separación) */
  font-weight: 700;
}

.google-toggle-btn .google-g {
  font-size: 1rem;
  line-height: 1;
  color: #4285f4;
}

.google-toggle-btn.active {
  background: #4285f4;
}

.google-toggle-btn.active .google-g {
  color: #fff;
}

/* Flecha al norte — esquina superior derecha del mapa, mismo estilo que los botones */
.north-arrow {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  width: 34px;
  height: 34px;
  padding: 3px;
  box-sizing: border-box;
  border-radius: 6px;
  background: var(--bg-primary);
  box-shadow: 0 1px 5px rgba(0,0,0,0.28), 0 1px 2px rgba(0,0,0,0.14);
  pointer-events: none;
}

.north-arrow img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
